import _ from 'lodash';
import { createSelector } from 'reselect';
import { decorateJob } from '@/utilities/jobAdmin.utilities';
import { type GetStoreState, type SetStoreState, createStore } from '@seeqdev/utils';
import type { CapabilityEnum, CapabilityInheritanceOutputV1 } from '@seeqdev/api/model/CapabilityInheritanceOutputV1';
import type { ChannelOutputV1 } from '@seeqdev/api/model/ChannelOutputV1';
import type { JobOutputV1 } from '@seeqdev/api/model/JobOutputV1';
import type { OrganizationOutputV1 } from '@seeqdev/api/model/OrganizationOutputV1';
import type { RequestOutputV1 } from '@seeqdev/api/model/RequestOutputV1';
import type { UserGroupOutputV1 } from '@seeqdev/api/model/UserGroupOutputV1';

interface AdminContactInfo {
  name: string;
  email: string;
}

interface AdministrationGroupDatasource {
  name?: string;
  datasourceId?: string;
  datasourceClass?: string;
}

export type AdministrationGroup = UserGroupOutputV1 & {
  datasource?: AdministrationGroupDatasource;
};

export interface AdministrationState {
  groups: AdministrationGroup[];
  capabilitiesInEditor: CapabilityInheritanceOutputV1[];
  adminContactInfo: AdminContactInfo;
  requests: RequestOutputV1[];
  subscriptions: ChannelOutputV1[];
  jobs: JobOutputV1[];
  externalOrganizations: OrganizationOutputV1[];
  externalOrganizationsMutationVersion: number;
  tooManyRequests?: boolean;
}

export interface AdministrationActions {
  setGroups: (groups: AdministrationGroup[]) => void;
  clearGroups: () => void;
  addGroup: (group: AdministrationGroup) => void;
  updateGroup: (group: Partial<AdministrationGroup> & Pick<AdministrationGroup, 'id'>) => void;
  removeGroups: (ids: Array<string | number>) => void;
  setCapabilitiesInEditor: (capabilities: CapabilityInheritanceOutputV1[]) => void;
  toggleCapabilitySelection: (capability: CapabilityEnum) => void;
  setAdminContactInfo: (adminContactInfo: AdminContactInfo) => void;
  setRequests: (requests: RequestOutputV1[]) => void;
  setSubscriptions: (subscriptions: ChannelOutputV1[]) => void;
  setJobs: (jobs: JobOutputV1[]) => void;
  setTooManyRequests: (tooManyRequests: boolean | undefined) => void;
  setExternalOrganizations: (externalOrganizations: OrganizationOutputV1[]) => void;
  bumpExternalOrganizationsMutationVersion: () => void;
  reset: () => void;
}

const initialState: AdministrationState = {
  groups: [],
  capabilitiesInEditor: [],
  adminContactInfo: {
    name: '',
    email: '',
  },
  requests: [],
  subscriptions: [],
  jobs: [],
  externalOrganizations: [],
  externalOrganizationsMutationVersion: 0,
  tooManyRequests: undefined,
};

const administrationActionsFactory = (
  set: SetStoreState<AdministrationState>,
  get: GetStoreState<AdministrationState>,
): AdministrationActions => ({
  setGroups: (groups) =>
    set((draft: AdministrationState) => {
      draft.groups = groups;
    }),

  clearGroups: () =>
    set((draft: AdministrationState) => {
      draft.groups = [];
    }),

  addGroup: (group) =>
    set((draft: AdministrationState) => {
      draft.groups.push(group);
    }),

  updateGroup: (group) =>
    set((draft: AdministrationState) => {
      const index = _.findIndex(draft.groups, ['id', group.id]);
      if (index > -1) {
        draft.groups[index] = {
          ...draft.groups[index],
          ...group,
        };
      }
    }),

  removeGroups: (ids) =>
    set((draft: AdministrationState) => {
      draft.groups = draft.groups.filter((group) => !ids.includes(group.id));
    }),

  setCapabilitiesInEditor: (capabilities) =>
    set((draft: AdministrationState) => {
      draft.capabilitiesInEditor = capabilities;
    }),

  toggleCapabilitySelection: (capability) =>
    set((draft: AdministrationState) => {
      const exists = _.some(draft.capabilitiesInEditor, { capability });
      draft.capabilitiesInEditor = exists
        ? _.reject(draft.capabilitiesInEditor, { capability })
        : _.concat(draft.capabilitiesInEditor, { capability });
    }),

  setAdminContactInfo: (adminContactInfo) =>
    set((draft: AdministrationState) => {
      draft.adminContactInfo = adminContactInfo;
    }),

  setRequests: (requests) =>
    set((draft: AdministrationState) => {
      draft.requests = requests;
    }),

  setSubscriptions: (subscriptions) =>
    set((draft: AdministrationState) => {
      draft.subscriptions = subscriptions;
    }),

  setJobs: (jobs) =>
    set((draft: AdministrationState) => {
      draft.jobs = jobs;
    }),

  setTooManyRequests: (tooManyRequests) =>
    set((draft: AdministrationState) => {
      draft.tooManyRequests = tooManyRequests;
    }),

  setExternalOrganizations: (externalOrganizations) =>
    set((draft: AdministrationState) => {
      draft.externalOrganizations = externalOrganizations;
    }),

  bumpExternalOrganizationsMutationVersion: () =>
    set((draft: AdministrationState) => {
      draft.externalOrganizationsMutationVersion += 1;
    }),

  reset: () => {
    set(() => initialState);
  },
});

const [AdministrationStoreProvider, useAdministrationStoreInstance, useAdministrationSelector] = createStore(
  'Administration',
  initialState,
  administrationActionsFactory,
);

export { AdministrationStoreProvider };

export const useAdministrationActions = () => useAdministrationStoreInstance().actions;
export const useAdministrationGroups = () => useAdministrationSelector((state) => state.groups);
export const useCapabilitiesInEditor = () => useAdministrationSelector((state) => state.capabilitiesInEditor);
export const useAdminContactInfo = () => useAdministrationSelector((state) => state.adminContactInfo);
export const useAdministrationRequests = () => useAdministrationSelector((state) => state.requests);
export const useAdministrationSubscriptions = () => useAdministrationSelector((state) => state.subscriptions);
export const useAdministrationJobs = () => useAdministrationSelector((state) => state.jobs);
const administrationDecoratedJobsSelector = createSelector([(state: AdministrationState) => state.jobs], (jobs) =>
  jobs.map((job) => decorateJob({ ...job })),
);
export const useAdministrationDecoratedJobs = () => useAdministrationSelector(administrationDecoratedJobsSelector);
export const useTooManyRequests = () => useAdministrationSelector((state) => state.tooManyRequests);
export const useExternalOrganizations = () => useAdministrationSelector((state) => state.externalOrganizations);
export const useExternalOrganizationsMutationVersion = () =>
  useAdministrationSelector((state) => state.externalOrganizationsMutationVersion);
